import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp as confirmSignUpAuth,
  getCurrentUser as currentAuthenticatedUser,
  fetchMFAPreference,
  resendSignUpCode,
  resetPassword as resetPasswordAuth,
  setUpTOTP,
  signIn as signInAuth,
  signOut,
  signUp as signUpAuth,
  updateMFAPreference,
  verifyTOTPSetup,
} from "@aws-amplify/auth";
import { updatePassword } from "aws-amplify/auth";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import i18n from "../../assets/i18n";
import { getRedirectPath } from "@repo/auth-utils/get-redirect-path";
import * as Sentry from "@sentry/react";
import { apiHandler, publicApiHandler } from "../../lib/apiHandler";
import {
  FORCE_CHANGE_PASSWORD,
  MFA_CONFIRMATION_REQUIRED,
  RESET_SIGNUP_FORM_DATA,
  SET_AUTH_DATA,
  SET_SIGNUP_DATA,
  SET_SIGNUP_FORM_DATA,
  SETUP_TOTP,
  TOTP_COMPLETE,
  USER_AUTHENTICATED,
  USER_INFO,
  USER_SIGNOUT,
} from "./reducer";

const fetchUserProfile = async (userData) => {
  return userData || (await fetchProfileInfo());
};

const calculateTrialDays = async (onTrail, trailExpire) => {
  if (onTrail && trailExpire) {
    let currentDate = new Date();
    return await date_diff_indays(currentDate, trailExpire);
  }
  return 0;
};

const fetchUserSubscribedPlans = async (orgId, onTrail) => {
  return await allSubscribedPlans(orgId, onTrail);
};

const fetchUserRolePermissions = async (roleId, isGluEmp) => {
  return await fetchPermissionData(roleId, isGluEmp);
};

const determineSubMainpageAccess = (rolePermission) => {
  return (
    (rolePermission.billingDetailsAccess?.[0]?.read ?? false) ||
    (rolePermission.stripePlansAccess?.[0]?.read ?? false) ||
    (rolePermission.planPurchasedAccess?.[0]?.read ?? false) ||
    (rolePermission.addCardAccess?.[0]?.read ?? false) ||
    (rolePermission.tokenBundleAccess?.[0]?.read ?? false) ||
    (rolePermission.userBundleAccess?.[0]?.read ?? false)
  );
};

const dispatchUserData = (
  dispatch,
  rolePermission,
  subsPlans,
  onTrail,
  hasInvoice,
  trailDayCount,
  subMainpageAccess,
) => {
  dispatch({
    type: SET_AUTH_DATA,
    payload: {
      myplanReadAccess: rolePermission.myplansAccess?.[0]?.read ?? false,
      myplanAllAccess: rolePermission.myplansAccess?.[0]?.all ?? false,
      myOrgReadAccess: rolePermission.myOrganisationAccess?.[0]?.read ?? false,
      myOrgAllAccess: rolePermission.myOrganisationAccess?.[0]?.all ?? false,
      myProfileReadAccess: rolePermission.myProfileAccess?.[0]?.read ?? false,
      myProfileAllAccess: rolePermission.myProfileAccess?.[0]?.all ?? false,
      myTeamReadAccess: rolePermission.myTeamAccess?.[0]?.read ?? false,
      myTeamAllAccess: rolePermission.myTeamAccess?.[0]?.all ?? false,
      settingsReadAccess: rolePermission.settingsAccess?.[0]?.read ?? false,
      settingsAllAccess: rolePermission.settingsAccess?.[0]?.all ?? false,
      adoptReadAccess: rolePermission.adoptAccess?.[0]?.read ?? false,
      adoptAllAccess: rolePermission.adoptAccess?.[0]?.all ?? false,
      purchaseReadAccess: rolePermission.purchaseAccess?.[0]?.read ?? false,
      purchaseAllAccess: rolePermission.purchaseAccess?.[0]?.all ?? false,
      channelPartnersReadAccess:
        rolePermission.channelPartnerAccess?.[0]?.read ?? false,
      channelPartnersAllAccess:
        rolePermission.channelPartnerAccess?.[0]?.all ?? false,

      billingDetailsReadAccess:
        rolePermission.billingDetailsAccess?.[0]?.read ?? false,
      billingDetailsAllAccess:
        rolePermission.billingDetailsAccess?.[0]?.all ?? false,
      stripePlansReadAccess:
        rolePermission.stripePlansAccess?.[0]?.read ?? false,
      stripePlansAllAccess: rolePermission.stripePlansAccess?.[0]?.all ?? false,
      planPurchasedReadAccess:
        rolePermission.planPurchasedAccess?.[0]?.read ?? false,
      planPurchasedAllAccess:
        rolePermission.planPurchasedAccess?.[0]?.all ?? false,
      addCardReadAccess: rolePermission.addCardAccess?.[0]?.read ?? false,
      addCardAllAccess: rolePermission.addCardAccess?.[0]?.all ?? false,
      invoiceReadAccess: rolePermission.invoiceAccess?.[0]?.read ?? false,
      invoiceAllAccess: rolePermission.invoiceAccess?.[0]?.all ?? false,

      moduleActivateAccess: subsPlans.moduleActivate,
      moduleAdoptAccess: subsPlans.moduleAdopt,
      modulePurchaseAccess: subsPlans.modulePurchase,
      moduleCIBAAccess: subsPlans.moduleCIBA,
      moduleRenewalAccess: subsPlans.moduleRenewal,
      moduleAdoptMyplanAccess: subsPlans.moduleAdopt,
      moduleActivateMyplanAccess: subsPlans.moduleActivate,
      moduleISightAccess: subsPlans.moduleISight,
      moduleReuseAccess: subsPlans.moduleReuse,

      tokenBundleReadAccess:
        rolePermission.tokenBundleAccess?.[0]?.read ?? false,
      tokenBundleAllAccess: rolePermission.tokenBundleAccess?.[0]?.all ?? false,
      userBundleReadAccess: rolePermission.userBundleAccess?.[0]?.read ?? false,
      userBundleAllAccess: rolePermission.userBundleAccess?.[0]?.all ?? false,

      kycModuleReadAccess: rolePermission.kycModuleAccess?.[0]?.read ?? false,
      kycModuleAllAccess: rolePermission.kycModuleAccess?.[0]?.all ?? false,

      renewalModuleReadAccess:
        rolePermission.renewalModuleAccess?.[0]?.read ?? false,
      renewalModuleAllAccess:
        rolePermission.renewalModuleAccess?.[0]?.all ?? false,

      netPulseReadAccess:
        rolePermission.netPulseModuleAccess?.[0]?.read ?? false,
      netPulseAllAccess: rolePermission.netPulseModuleAccess?.[0]?.all ?? false,

      dashBoardReadAccess: rolePermission.dashBoardAcess?.[0]?.read ?? false,
      dashBoardAllAccess: rolePermission.dashBoardAcess?.[0]?.all ?? false,

      opportunityReadAccess:
        rolePermission.opportunityAcess?.[0]?.read ?? false,
      opportunityAllAccess: rolePermission.opportunityAcess?.[0]?.all ?? false,

      activeDealsReadAccess:
        rolePermission.activeDealsAccess?.[0]?.read ?? false,
      activeDealsAllAccess: rolePermission.activeDealsAccess?.[0]?.all ?? false,

      csmFocusReadAccess: rolePermission.csmFocusAccess?.[0]?.read ?? false,
      csmFocusAllAccess: rolePermission.csmFocusAccess?.[0]?.all ?? false,

      psCalculatorReadAccess:
        rolePermission.psCalculatorAccess?.[0]?.read ?? false,
      psCalculatorAllAccess:
        rolePermission.psCalculatorAccess?.[0]?.all ?? false,

      siteInABoxReadAccess: rolePermission.siteInABoxAccess?.[0]?.read ?? false,
      siteInABoxAllAccess: rolePermission.siteInABoxAccess?.[0]?.all ?? false,

      reuseReadAccess: rolePermission.reuseAccess?.[0]?.read ?? false,
      reuseAllAccess: rolePermission.reuseAccess?.[0]?.all ?? false,

      iSightReadAccess: rolePermission.iSightAccess?.[0]?.read ?? false,
      iSightAllAccess: rolePermission.iSightAccess?.[0]?.all ?? false,

      loaModuleReadAccess: rolePermission.loaModuleAccess?.[0]?.read ?? false,
      loaModuleAllAccess: rolePermission.loaModuleAccess?.[0]?.all ?? false,

      rolePermission: rolePermission.userDatas,
      allPaidActivePlans: subsPlans.plans,
      monthlyPlan: subsPlans.monthlyPlan,
      mainSubscribeId: subsPlans.mainSubscribeId,
      accessModules: subsPlans.accessModules,
      subMainpageAccess,
      onTrail,
      hasInvoice,
      trailDayCount,
    },
  });
};

const dispatchUserInfo = (dispatch, user_session, profile) => {
  dispatch({
    type: USER_INFO,
    payload: {
      user: user_session,
      profile,
      full_name: profile?.full_name || "",
      mobile_number: profile?.mobile_number || "",
      email: profile?.email || "",
    },
  });
};

const NOT_SIGNED_IN_ERRORS = [
  "UserUnAuthenticatedException",
  "NotAuthorizedException",
];

export const getCurrentUser = (userData) => async (dispatch) => {
  let user_session;
  try {
    user_session = await currentAuthenticatedUser({
      bypassCache: true,
    });
  } catch (error) {
    dispatch(setAuthData("authLoading", false));
    // No session, or an expired one: the visitor simply isn't signed in
    if (NOT_SIGNED_IN_ERRORS.includes(error?.name)) return;
    console.error(error);
    Sentry.captureException(error, {
      extra: { context: "currentAuthenticatedUser" },
    });
    toast.error(
      i18n.t("We couldn't verify your login. Please refresh the page."),
    );
    return;
  }
  try {
    const profile = await fetchUserProfile(userData);

    const trialDayCount = await calculateTrialDays(
      profile.parent_org?.on_trail,
      profile.parent_org?.trail_expire,
    );

    const subsPlans = await fetchUserSubscribedPlans(
      profile.org_id,
      profile.parent_org?.on_trail,
    );

    const rolePermissions = await fetchUserRolePermissions(
      profile.role_id,
      profile.is_glu_emp,
    );

    const subMainpageAccess = determineSubMainpageAccess(rolePermissions);

    dispatchUserData(
      dispatch,
      rolePermissions,
      subsPlans,
      profile.parent_org?.on_trail,
      profile.parent_org?.has_invoice,
      trialDayCount,
      subMainpageAccess,
    );

    dispatchUserInfo(dispatch, user_session, profile);
    dispatch(setAuthData("authLoadFailed", false));
  } catch (error) {
    console.error(error);
    Sentry.captureException(error, { extra: { context: "getCurrentUser" } });
    // Pages show "refresh" instead of "Please purchase a plan" while this is set
    dispatch(setAuthData("authLoadFailed", true));
    dispatch(setAuthData("authLoading", false));
    toast.error(
      i18n.t("We couldn't load your subscription. Please refresh the page."),
    );
  }
};

const languageOptions = [
  { key: "34", languageCode: "ar" },
  { key: "1", languageCode: "en" },
  { key: "10", languageCode: "fr" },
  { key: "13", languageCode: "de" },
  { key: "35", languageCode: "he" },
  { key: "3", languageCode: "hi" },
  { key: "14", languageCode: "it" },
  { key: "8", languageCode: "pt" },
  { key: "4", languageCode: "es" },
];

const fetchProfileInfo = async () => {
  const req_params = {};
  const user_profile = await apiHandler("get", "/user/profile", req_params);

  if (
    user_profile?.data &&
    (!localStorage.getItem("language") ||
      user_profile.data.parent_org.language_id !=
        localStorage.getItem("org_language"))
  ) {
    localStorage.setItem("language", user_profile.data.parent_org.language_id);
    localStorage.setItem(
      "org_language",
      user_profile.data.parent_org.language_id,
    );
  }
  let lanCode = localStorage.getItem("language");
  const languagefilter = languageOptions.filter((item) => item.key == lanCode);
  const languageCode =
    languagefilter.length == 0 ? "en" : languagefilter[0].languageCode;
  localStorage.setItem("languageCode", languageCode);
  return user_profile?.data || "";
};

const loginTrack = async (id, status) => {
  const req = {
    id,
    ...(status && { status }), // Add status directly if it exists
  };

  return apiHandler("post", `/user/login-activity-track`, req);
};

export const signUp =
  ({ family_name, given_name, phone_number, email, password, navigate }) =>
  async (dispatch) => {
    try {
      const emailLower = (email || "").trim().toLowerCase();
      let username = uuidv4();
      const { user } = await signUpAuth({
        username,
        password,
        options: {
          userAttributes: {
            email: emailLower,
            phone_number,
            family_name,
            given_name,
          },
        },
      });

      if (user) {
        dispatch(setAuthData("user", user));
        navigate(
          "/account-verification/signup?uid=" + username + "&mode=register",
        );
      }
    } catch (error) {
      toast.error(error.message || i18n.t("Something went wrong, try again"));
    }
  };

export const setAuthData = (name, value) => (dispatch) => {
  dispatch({
    type: SET_AUTH_DATA,
    payload: { [name]: value },
  });
};

export const confirmSignUp =
  ({ username, code }) =>
  (dispatch) => {
    confirmSignUpAuth({ username, confirmationCode: code })
      .then(() => {
        toast.success(i18n.t("Email verified"));
        dispatch(setAuthData("isEmailVerified", true));
      })
      .catch((error) => {
        toast.error(error.message || i18n.t("Something went wrong, try again"));
      });
  };

export const resendSignUp = (username) => async (_dispatch) => {
  try {
    await resendSignUpCode({ username });
    toast.success(i18n.t("Verification code has been sent to your email"));
  } catch (error) {
    toast.error(error.message || i18n.t("Something went wrong, try again"));
  }
};
const USER_LIMIT_REACHED_MESSAGE =
  "Your organisation has no available user seats. Please ask your administrator to free one up or contact GLU admin.";
const INVITE_SIGNUP_ERROR_KEY = "cannot_complete_registration";

export const otpVerification =
  ({ username, confirmationCode, password, navigate, mfaRetry }) =>
  async (dispatch) => {
    const signInUsername = (username ?? "").trim();
    const otp = (confirmationCode ?? "").trim();
    try {
      const result = await Promise.resolve(checkUserLimit(signInUsername));
      if (!result) return;
      dispatch(setAuthData("disable_signIn_button", true));
      const { nextStep } = await signInAuth({
        username: signInUsername,
        password: otp,
      });

      if (
        nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        await confirmSignIn({ challengeResponse: password });
      }
      dispatch(setAuthData("otp_verify", true));
      await dispatch(getCurrentUser());
      let userData = await fetchProfileInfo();
      await loginTrack(userData.id, "active");
      dispatch(setAuthData("disable_signIn_button", false));
      toast.success(i18n.t("Welcome to GLU"));
      navigate("/dashboard/profile-manager/edit");
    } catch (error) {
      console.error("[InviteFlow] otpVerification failed", {
        error: error?.name || error?.message || String(error),
      });
      Sentry.captureException(error, {
        tags: { flow: "invite_signup", reason: "otp_or_password_failed" },
      });
      toast.error(i18n.t(INVITE_SIGNUP_ERROR_KEY));
      dispatch(setAuthData("disable_signIn_button", false));
      dispatch(setAuthData("otp_verify", false));
    }
  };

const isMfaRequired = (user) =>
  user.challengeName === "MFA_REQUIRED" ||
  user.challengeName === "SMS_MFA" ||
  user.challengeName === "SOFTWARE_TOKEN_MFA" ||
  user.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ||
  user.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE";

const handleNewPasswordRequired = (
  dispatch,
  user,
  username,
  password,
  mfaRetry,
) =>
  dispatch({
    type: FORCE_CHANGE_PASSWORD,
    payload: {
      cognitoUser: user,
      temp_user_info: { username, password, retry: (mfaRetry || 0) + 1 },
    },
  });

const handleMfaRequired = (dispatch, user, username, password, mfaRetry) =>
  dispatch({
    type: MFA_CONFIRMATION_REQUIRED,
    payload: {
      cognitoUser: user,
      temp_user_info: { username, password, retry: (mfaRetry || 0) + 1 },
    },
  });

const handleAuthenticatedUser = async (
  dispatch,
  user,
  userData,
  navigate,
  location,
) => {
  if (userData.is_glu_emp) {
    toast.error(i18n.t("Unauthorized Access"));
    await signOut();
    return false;
  }
  if (userData.status == "inactive") {
    toast.error(i18n.t("This user is deactivated.Please contact GLU admin"));
    await signOut();
    dispatch(setAuthData("disable_signIn_button", false));
    return true;
  }
  await loginTrack(userData.id);
  await dispatch(getCurrentUser(userData));
  dispatch({ type: USER_AUTHENTICATED, payload: user });
  toast.success(i18n.t("Welcome to GLU"));
  if (userData.mobile_number) {
    dispatch(setAuthData("mobile_number_add", true));
  }
  navigate(getRedirectPath(location));
  return false;
};

const formatSignInErrorMessage = async (error, dispatch) => {
  console.error(error);
  const msg = {
    "PreAuthentication failed with error User Disabled.": "User Disabled",
    "PreAuthentication failed with error User Not Found.": "User Not Found",
  };
  let message = msg[error.message] ? msg[error.message] : error.message;
  if (
    message.includes("already a signed in user") ||
    message.includes("There is already a signed in user") ||
    message.toLowerCase().includes("userunauthenticatedexception")
  ) {
    try {
      await signOut();
      dispatch({ type: USER_SIGNOUT });
      message =
        "A session was already active. You have been signed out. Please sign in again.";
    } catch (signOutErr) {
      console.error("signOut in signIn catch failed:", signOutErr);
      message =
        "A session is already active and we could not sign you out. Please refresh the page and try again.";
    }
  }
  if (message.includes("PhoneNumber Reason")) {
    message =
      "SMS verification failed with invalid Mobile number. Please Contact admin to change Mobile number.";
  }
  return i18n.t(message) || i18n.t("Something went wrong, try again");
};

export const signIn =
  ({ username, password, navigate, mfaRetry, location }) =>
  async (dispatch) => {
    try {
      if (mfaRetry && mfaRetry > 3) {
        toast.error(i18n.t("Maximum retries reached"));
      }
      dispatch(setAuthData("disable_signIn_button", true));

      const user = await signInAuth({ username, password });

      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        handleNewPasswordRequired(dispatch, user, username, password, mfaRetry);
      } else if (isMfaRequired(user)) {
        handleMfaRequired(dispatch, user, username, password, mfaRetry);
      } else {
        const userData = await fetchProfileInfo();
        const earlyExit = await handleAuthenticatedUser(
          dispatch,
          user,
          userData,
          navigate,
          location,
        );
        if (earlyExit) return;
      }
      dispatch(setAuthData("disable_signIn_button", false));
    } catch (error) {
      const message = await formatSignInErrorMessage(error, dispatch);
      toast.error(message);
      dispatch(setAuthData("disable_signIn_button", false));
    }
  };

export const resendVerificationCode =
  ({ username, password, mfaRetry }) =>
  async (dispatch) => {
    try {
      if (mfaRetry && mfaRetry > 2) {
        toast.error(i18n.t("Maximum retries reached"));
        dispatch(setAuthData("mfa", false));
        return;
      }

      const user = await signInAuth({ username, password });
      if (
        user?.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ||
        user?.nextStep?.signInStep === "SMS_MFA" ||
        user?.nextStep?.signInStep === "SOFTWARE_TOKEN_MFA"
      ) {
        dispatch({
          type: MFA_CONFIRMATION_REQUIRED,
          payload: {
            cognitoUser: user,
            temp_user_info: {
              username: username,
              password: password,
              retry: (mfaRetry || 0) + 1,
            },
          },
        });
        const destination =
          user?.nextStep?.codeDeliveryDetails?.destination || "your device";
        toast.success(
          `${i18n.t("verification code has been sent to")} ${destination}`,
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(i18n.t("Maximum retries reached"));
    }
  };

export const requestPasswordReset =
  ({ username, DisableBtn }) =>
  async (dispatch) => {
    try {
      const req_params = {
        email: username,
      };
      let response = await publicApiHandler(
        "post",
        `/user/user-by-email`,
        req_params,
      );
      if (response.data && !response.data.is_glu_emp) {
        await resetPasswordAuth({
          username,
        });
        dispatch(setAuthData("isResetMailSend", true));
      } else {
        DisableBtn(false);
        toast.error(i18n.t("User not found"));
      }
    } catch (error) {
      toast.error(error.message || i18n.t("Something went wrong, try again"));
    }
  };

export const resetPassword =
  ({ username, code, password }) =>
  async (dispatch) => {
    try {
      await confirmResetPassword({
        username,
        newPassword: password,
        confirmationCode: code,
      });
      dispatch(setAuthData("passwordResetComplete", true));
    } catch (error) {
      dispatch(setAuthData("passwordResetLinkExpired", true));
      toast.error(i18n.t("Link Expired"));
    }
  };

export const confirmLogin =
  ({ code, navigate, passwordAttempts, location }) =>
  async (dispatch) => {
    try {
      dispatch(setAuthData("disable_signIn_button", true));
      dispatch(setAuthData("passwordAttempts", passwordAttempts + 1));
      let user = await confirmSignIn({ challengeResponse: code });
      // user authenticated
      dispatch({ type: USER_AUTHENTICATED, payload: user });
      //set user session to redux
      dispatch(getCurrentUser());
      let userData = await fetchProfileInfo();
      if (userData.status == "inactive") {
        toast.error(
          i18n.t("This user is deactivated.Please contact GLU admin"),
        );
        await signOut();
        await dispatch(setAuthData("disable_signIn_button", true));
        setTimeout(() => {
          window.location.replace("/sign-in");
        }, 2500);
        return true;
      }

      await loginTrack(userData.id);
      dispatch(setAuthData("disable_signIn_button", false));
      toast.success(i18n.t("Welcome to GLU"));
      const redirectPath = getRedirectPath(location);
      navigate(redirectPath);
    } catch (err) {
      dispatch(setAuthData("disable_signIn_button", false));
      if (passwordAttempts > 2) {
        toast.error(i18n.t("Maximum retries reached please login again"));
        dispatch(setAuthData("mfa", false));
        dispatch(setAuthData("passwordAttempts", 0));
        return;
      }
      toast.error(i18n.t("Incorrect password please try again"));
    }
  };

export const setNewPassword =
  ({ password, navigate, location }) =>
  async (dispatch) => {
    try {
      let user = await confirmSignIn({ challengeResponse: password });
      // inspect response and check whether
      // 1. MFA confirmation is required, dispatch->MFA_CONFIRMATION_REQUIRED
      // 2. otherwise, authenticate user, dispatch -> USER_AUTHENTICATED
      if (
        user.challengeName === "SMS_MFA" ||
        user.challengeName === "SOFTWARE_TOKEN_MFA"
      ) {
        dispatch({
          type: MFA_CONFIRMATION_REQUIRED,
          payload: {
            cognitoUser: { ...user },
            temp_user_info: { username: user.username, password },
          },
        });
      } else {
        // user authenticated
        dispatch({ type: USER_AUTHENTICATED, payload: user });
        dispatch(getCurrentUser());
        let userData = await fetchProfileInfo();
        await loginTrack(userData.id);
        toast.success(i18n.t("Welcome to GLU"));
        const redirectPath = getRedirectPath(location);
        navigate(redirectPath);
      }
    } catch (err) {
      console.error(
        "actions.setNewPassword():Auth.completeNewPassword() err:",
        err,
      );
      toast.error(err.message || i18n.t("Something went wrong, try again"));
    }
  };

export const logout = (navigate) => async (dispatch) => {
  try {
    await signOut();
    dispatch({ type: USER_SIGNOUT });
    navigate("/");
  } catch (err) {
    console.error("signOut-error", err);
    toast.error(err.message || i18n.t("Something went wrong, try again"));
  }
};

export const getPreferredMFA = () => async (dispatch) => {
  try {
    let mfaSettings = await fetchMFAPreference();
    const smsValue = mfaSettings?.enabled?.includes("TOTP")
      ? "TOTP"
      : mfaSettings?.enabled?.includes("SMS")
        ? "SMS"
        : "NOMFA";
    dispatch(setAuthData("mfa_mode", smsValue));
  } catch (err) {
    console.error("error", err);
  }
};

export const setupTOTP = (user) => async (dispatch) => {
  try {
    // To setup TOTP, first you need to get a `authorization code` from Amazon Cognito
    // `user` is the current Authenticated user
    let code = await setUpTOTP();
    // You can directly display the `code` to the user or convert it to a QR code to be scanned.
    // E.g., use following code sample to render a QR code with `qrcode.react` component:

    const totp_connection_string =
      "otpauth://totp/AWSCognito:" +
      user.username +
      "?secret=" +
      code.sharedSecret +
      "&issuer=" +
      "GLU.CX";
    dispatch({ type: SETUP_TOTP, payload: totp_connection_string });
  } catch (err) {
    toast.error(err.message || i18n.t("Something went wrong, try again"));
  }
};

export const verifyTotpToken =
  ({ user, code }) =>
  async (dispatch) => {
    try {
      await verifyTOTPSetup({ code });

      // don't forget to set TOTP as the preferred MFA method
      await updateMFAPreference({ totp: "PREFERRED" });
      toast.success(i18n.t("TOTP MFA enabled successfully"));
      dispatch({ type: TOTP_COMPLETE });
      dispatch(getPreferredMFA());
    } catch (err) {
      toast.error(
        i18n.t("Code mismatch and failed to enable software token MFA"),
      );
    }
  };

export const disableMfa =
  (currentMfa = "") =>
  async (dispatch) => {
    try {
      if (currentMfa === "TOTP") {
        await updateMFAPreference({ totp: "DISABLED" });
      }
      if (currentMfa === "SMS") {
        await updateMFAPreference({ sms: "DISABLED" });
      }
      toast.success(currentMfa + i18n.t(" MFA disabled successfully"));
      dispatch(getPreferredMFA());
    } catch (err) {
      toast.error(err.message || i18n.t("Something went wrong, try again"));
    }
  };

export const setupSmsMfa = () => async (dispatch) => {
  try {
    await updateMFAPreference({ sms: "ENABLED" });
    toast.success(i18n.t("SMS MFA enabled successfully"));
    dispatch(getPreferredMFA());
  } catch (err) {
    toast.error(err.message || i18n.t("Something went wrong, try again"));
  }
};

export const verifyResetLink = (token) => async (dispatch) => {
  try {
    const req_params = {
      token,
    };
    let response = await publicApiHandler(
      "post",
      "/user/token_reset",
      req_params,
    );
    let token_details = response?.data;
    if (token_details?.cognito_id) {
      dispatch(setAuthData("passwordResetLinkValidation", false));
    } else {
      dispatch(setAuthData("passwordResetLinkExpired", true));
    }
  } catch (err) {
    //Token is not verified

    dispatch(setAuthData("passwordResetLinkExpired", true));
  }
};

export const verifyTokenExpiration = (token) => async (dispatch) => {
  try {
    const reqId = new URLSearchParams(window.location.search).get("req_id");
    const reqIdTrimmed = (reqId ?? "").trim();
    if (!token) {
      console.warn("[InviteFlow] verifyTokenExpiration:noTokenInUrl", {
        reqId,
        path: window.location.pathname,
        search: window.location.search,
        hint: "Invite links must include ?token= on /otp-verification",
      });
      dispatch(setAuthData("tokenVetificationStatus", "EXPIRED"));
      return;
    }
    const req_params = {
      token,
    };
    let { code, data } = await publicApiHandler(
      "post",
      "/user/check-expiration",
      req_params,
    );
    let { status, user } = data ?? {};
    const emailFromInviteUrl = new URLSearchParams(window.location.search).get(
      "email",
    );
    const resolvedEmail =
      (user?.email && String(user.email).trim()) || emailFromInviteUrl || "";
    console.info("[InviteFlow] verifyTokenExpiration:response", {
      reqId,
      code,
      status,
      hasUser: Boolean(user),
      email: resolvedEmail || null,
      usedInviteUrlEmail: Boolean(!user?.email && emailFromInviteUrl),
    });

    if (code != 200 || status == "expired") {
      dispatch(setAuthData("tokenVetificationStatus", "EXPIRED"));
      return;
    }
    if (status == "active" && (resolvedEmail || reqIdTrimmed)) {
      if (resolvedEmail) {
        dispatch(setAuthData("verifyingUserName", resolvedEmail));
      }
      dispatch(setAuthData("tokenVetificationStatus", "VALID"));
      return;
    }
    console.warn("[InviteFlow] Active token missing user email and req_id", {
      reqId,
      status,
    });
    dispatch(setAuthData("tokenVetificationStatus", "EXPIRED"));
  } catch (err) {
    const reqId = new URLSearchParams(window.location.search).get("req_id");
    console.error("[InviteFlow] verifyTokenExpiration:error", { reqId, err });
    dispatch(setAuthData("tokenVetificationStatus", "EXPIRED"));
  }
};

// ...

export const updateProfile =
  ({ full_name, email, mobile_number }) =>
  async (dispatch) => {
    try {
      dispatch(setAuthData("profileUpdateBtnDisable", true));
      const req_params = {
        full_name,
        email,
        mobile_number,
      };
      await apiHandler("put", "/user/profile/update", req_params);
      toast.success(i18n.t("Profile updated successfully"));
      dispatch(getCurrentUser());
      dispatch(setAuthData("profileUpdateBtnDisable", false));
    } catch (err) {
      let errMessage =
        err?.message || i18n.t("Something went wrong, try again");
      toast.error(errMessage);
      dispatch(setAuthData("profileUpdateBtnDisable", false));
    }
  };

export const handleUpdatePassword =
  ({ oldPassword, newPassword }) =>
  async (dispatch) => {
    try {
      dispatch(setAuthData("passwordUpdateBtnDisable", true));
      await updatePassword({ oldPassword, newPassword });

      toast.success(i18n.t("Password updated successfully"));
      dispatch({
        type: SET_AUTH_DATA,
        payload: {
          password: "",
          oldPassword: "",
          newPassword: "",
        },
      });
    } catch (err) {
      let errMessage =
        err?.message || i18n.t("Something went wrong, try again");
      toast.error(i18n.t(errMessage));
      dispatch(setAuthData("passwordUpdateBtnDisable", false));
    }
  };

export const openPasswordOtpPopup = () => async (dispatch) => {
  dispatch({
    type: SET_AUTH_DATA,
    payload: {
      change_password_otp_popup: true,
    },
  });
};

export const hidePasswordOtpPopup = () => async (dispatch) => {
  dispatch({
    type: SET_AUTH_DATA,
    payload: {
      change_password_otp_popup: false,
    },
  });
};

export const verifyPasswordOtp = (body, submit) => async (dispatch) => {
  try {
    dispatch({
      type: SET_AUTH_DATA,
      payload: { change_password_disable: true },
    });
    let req = body;
    await apiHandler("post", `/user/verify-otp`, req);
    dispatch({
      type: SET_AUTH_DATA,
      payload: {
        change_password_otp_popup: false,
        change_password_disable: false,
      },
    });
    submit();
  } catch (err) {
    dispatch({
      type: SET_AUTH_DATA,
      payload: { change_password_disable: false },
    });
    let errMessage = err?.message || i18n.t("Something went wrong, try again");
    toast.error(i18n.t(errMessage));
  }
};

export const getPasswordOtp = (body) => async (dispatch) => {
  try {
    let req = body;
    await apiHandler("post", `/user/sent-otp`, req);
  } catch (err) {
    let errMessage = err?.message || i18n.t("Something went wrong, try again");
    toast.error(i18n.t(errMessage));
  }
};

const fetchPermissionData = async (roleId, isGlueEmp) => {
  const req_params = {
    roleId,
    isGlueEmp,
  };
  let userPermissions = await apiHandler(
    "post",
    "/user/role-permissions",
    req_params,
  );
  let resData = userPermissions?.data || "";
  return resData;
};

export const SendLogin = () => async (dispatch) => {
  try {
    return await apiHandler("get", `/user/addloginactivity`);
  } catch (err) {
    console.error(err);
    Sentry.captureException(err, { extra: { context: "SendLogin" } });
  }
};

export const allSubscribedPlans = async (orgId, onTrail) => {
  const req_body = {
    partnerId: orgId,
  };
  const response = await apiHandler(
    "post",
    "/payment/partner_active_plans",
    req_body,
  );
  let monthlyPlan = false;
  let mainSubscribeId = "";
  const subsPlans = Array.isArray(response?.data) ? response.data : [];
  const accessModules = [];

  if (subsPlans.length > 0) {
    const promiseOne = subsPlans.map(async (items) => {
      if (!items?.price_plan) {
        Sentry.captureMessage("Subscription has no price_plan; skipped", {
          level: "warning",
          extra: { subscriptionId: items?.subscription_id, orgId },
        });
        return;
      }
      if (
        items.plan_type === "recurring" &&
        items.price_plan?.plan_interval === "month" &&
        !items.cancel_at_period_end &&
        items.payment_mode === "online"
      ) {
        monthlyPlan = true;
        mainSubscribeId = items.subscription_id;
      }
      const planModules = items.price_plan?.modules ?? [];
      const modules = planModules.map(async (dt) => {
        const moduleName = dt?.modules_names?.modules;
        if (!moduleName) {
          Sentry.captureMessage("Plan module has no name; access skipped", {
            level: "warning",
            extra: { pricePlanId: items.price_plan?.id, orgId },
          });
          return;
        }
        if (!accessModules.includes(moduleName)) {
          accessModules.push(moduleName);
        }
      });
      await Promise.all(modules);
    });
    await Promise.all(promiseOne);
  }
  /* module permissions */
  let moduleActivate = false,
    moduleAdopt = false,
    modulePurchase = false,
    moduleCIBA = false,
    moduleRenewal = false,
    moduleISight = false,
    moduleReuse = false;
  moduleActivate = accessModules.includes("Activate") || onTrail;
  moduleAdopt = accessModules.includes("Adopt") || onTrail;
  modulePurchase = accessModules.includes("Purchase") || onTrail;
  moduleCIBA =
    accessModules.includes("Customer Install Base Analysis") || onTrail;
  moduleRenewal = accessModules.includes("Renewal") || onTrail;
  moduleISight = accessModules.includes("iSight") || onTrail;
  moduleReuse = accessModules.includes("Reuse") || onTrail;

  return {
    plans: subsPlans,
    monthlyPlan,
    mainSubscribeId,
    accessModules,
    moduleActivate,
    moduleAdopt,
    modulePurchase,
    moduleCIBA,
    moduleRenewal,
    moduleISight,
    moduleReuse,
  };
};

const date_diff_indays = async (date1, date2) => {
  const dt1 = new Date(date1);
  const dt2 = new Date(date2);
  return Math.floor(
    (Date.UTC(dt2.getFullYear(), dt2.getMonth(), dt2.getDate()) -
      Date.UTC(dt1.getFullYear(), dt1.getMonth(), dt1.getDate())) /
      (1000 * 60 * 60 * 24),
  );
};

//signUp Module
export const setSignUpFormData = (name, value) => {
  return {
    type: SET_SIGNUP_FORM_DATA,
    payload: {
      name,
      value,
    },
  };
};

export const resetSignUpFields = () => (dispatch) => {
  dispatch({ type: RESET_SIGNUP_FORM_DATA });
};

const returnDropDownList = (data, elementType) => {
  const temp = [];
  let dropdownOption;
  if (elementType != "country_name") {
    data?.map((element) => {
      dropdownOption = { key: element.id, text: element[elementType] };
      temp.push(dropdownOption);
    });
  } else {
    data?.map((element) => {
      dropdownOption = {
        key: element.id,
        text: element[elementType],
        region: element["region_id"],
      };
      temp.push(dropdownOption);
    });
  }
  return temp;
};

export const getSignUpFormData = () => async (dispatch) => {
  dispatch(disableSignUpButton(true));

  try {
    const result = await publicApiHandler("get", "/tenant/get-signup-details");

    const { countryOfOperations, languages, organisationTypes, regions } =
      result.data;

    const listCountryOfOperations = returnDropDownList(
      countryOfOperations,
      "country_name",
    );
    const listRegions = returnDropDownList(regions, "region_name");
    const listLanguages = returnDropDownList(languages, "name");
    const listOrganisationTypes = returnDropDownList(
      organisationTypes,
      "org_type",
    );

    dispatch({
      type: SET_SIGNUP_DATA,
      payload: {
        drop_down_country_of_operations: listCountryOfOperations,
        drop_down_regions: listRegions,
        drop_down_Languages: listLanguages,
        drop_down_organisation_types: listOrganisationTypes,
      },
    });
  } catch (error) {
    console.error(error);
  }
};

export const submitSignupData =
  (body, navigate, disable_submit_button, drop_down_regions) =>
  async (dispatch) => {
    // country_operation_id is region_id
    try {
      const {
        new_user_email,
        new_user_contact_person_name,
        new_user_org_name,
        new_user_phone_number,
        new_user_region_id,
        new_user_country_operation_id,
        new_user_language_id,
        cx_specialized,
      } = body;
      let region;
      if (drop_down_regions.length > 1) {
        region = Number(new_user_region_id);
      } else {
        region = Number(drop_down_regions[0]["key"]);
      }
      const emailLower = (new_user_email || "").trim().toLowerCase();
      const data = {
        organisation_type_id: 1,
        country_operation_id: new_user_country_operation_id,
        country_id: region,
        language_id: new_user_language_id,
        org_name: new_user_org_name,
        email: emailLower,
        phone_number: new_user_phone_number,
        contact_person_name: new_user_contact_person_name,
        plan_id: 0,
        cisco_cx_specialized: cx_specialized,
      };
      const response = await publicApiHandler("post", "/tenant/sign-up", data);
      toast.success(
        i18n.t(response.message) ||
          i18n.t("Signup Complete, Please check your email"),
      );
      dispatch(disableSignUpButton(disable_submit_button));
      navigate("/sign-up/success");
      return;
    } catch (error) {
      const errorMessage = i18n.t(error?.message) || i18n.t("Unable to signup");
      dispatch(disableSignUpButton(!disable_submit_button));
      toast.error(errorMessage);
    }
  };

export const disableSignUpButton = (data) => async (dispatch) => {
  dispatch({
    type: SET_SIGNUP_DATA,
    payload: {
      disable_submit_button: !data,
    },
  });
};
//signUp Module

export const getPartnersWalletBalance = () => async (dispatch) => {
  try {
    dispatch({ type: SET_AUTH_DATA, payload: { kycReportLoading: true } });
    let response = await apiHandler("get", `/payment/token-balance`);
    dispatch({
      type: SET_AUTH_DATA,
      payload: {
        walletBalance: response?.data.balance,
        kycReportLoading: false,
      },
    });
  } catch (err) {
    dispatch({ type: SET_AUTH_DATA, payload: { kycReportLoading: false } });

    toast.error(i18n.t(err.message || "Something went wrong, try again"));
  }
};

/**
 * check if user's organisation has limit before activating user
 * @returns {boolean} True or false if an error occurs.
 */
export const checkUserLimit = async (userName) => {
  try {
    const FAILED_MESSAGE =
      "We regret to inform you that the registration process has failed. Please contact GLU admin.";
    const response = await publicApiHandler("post", "/user/check-user-limit", {
      userName,
    });
    const result = response?.data == "true" ? true : false;
    if (!result) {
      toast.error(
        i18n.t(
          response?.message === "user_limit_reached"
            ? USER_LIMIT_REACHED_MESSAGE
            : FAILED_MESSAGE,
        ),
      );
    }
    return result;
  } catch (err) {
    toast.error(i18n.t(err.message || "Something went wrong, try again"));
  }
};
